import type {
  RoutineDifficultyOptionsDTO,
  RoutineDifficultyOptions,
  TreatmentOverviewDTO,
  MistakeText,
} from "@akina/domain";
import { toCamelCase } from "@akina/utils";
import type {
  ReviewTaskDTO,
  ReviewTaskDTOApi,
  FeedbacksSummaryDTO,
  FeedbacksSummaryDTOApi,
  TreatmentSummaryResponse,
  TreatmentSummaryResponseApi,
  TherapistFeedback,
  TherapistFeedbackTypeEnum,
} from "types/schemas";
import { ApiMethod, type ApiConfiguration } from "..";
import type { CMSStory } from "../adapters";
import { CMS_PATHS, slugifyCmsKey, type CmsStoryContent } from "../adapters";
import { routineDifficultyOptionsMapper } from "../mappers";
import { uploadFileToS3 } from "./shared";

export const sessionReviewApi = (config: ApiConfiguration) => ({
  async uploadFeedback(params: {
    sessionId: string;
    mediaType: TherapistFeedbackTypeEnum | null | undefined;
    routineId?: number;
    file: File;
  }): Promise<string> {
    const response = await config.httpService.fetch<{
      presigned_url: string;
      media_object_name: string;
    }>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.mediaStorage(params.sessionId, params.mediaType, params.routineId),
    });

    await uploadFileToS3(response.presigned_url, params.file);

    return response.media_object_name;
  },
  async addSessionFeedback(params: { sessionId: string; feedback: TherapistFeedback; file?: File }): Promise<string> {
    if (params.file && params.feedback.mediaType) {
      const mediaObjectName = await this.uploadFeedback({
        sessionId: params.sessionId,
        mediaType: params.feedback.mediaType,
        file: params.file,
      });

      await config.httpService.fetch({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.sessionFeedback,
        data: {
          session_log_id: params.sessionId,
          general_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
            media_object_name: mediaObjectName,
          },
        },
      });
    } else {
      await config.httpService.fetch({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.sessionFeedback,
        data: {
          session_log_id: params.sessionId,
          general_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
          },
        },
      });
    }

    return params.sessionId;
  },
  async deleteSessionFeedback(sessionId: string): Promise<string> {
    await config.httpService.fetch<{
      treatments: TreatmentOverviewDTO[];
    }>({
      method: ApiMethod.DELETE,
      url: config.endpoints.sessionReview.sessionFeedback,
      data: {
        session_log_id: sessionId,
      },
    });

    return sessionId;
  },
  async addRoutineFeedback(params: {
    sessionId: string;
    routineId: number;
    feedback: TherapistFeedback;
    file?: File;
  }): Promise<string> {
    if (params.file && params.feedback.mediaType) {
      const mediaObjectName = await this.uploadFeedback({
        sessionId: params.sessionId,
        routineId: params.routineId,
        mediaType: params.feedback.mediaType,
        file: params.file,
      });

      await config.httpService.fetch<{
        treatments: TreatmentOverviewDTO[];
      }>({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.routineFeedback.base,
        data: {
          session_log_id: params.sessionId,
          routine_log_id: params.routineId,
          routine_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
            media_object_name: mediaObjectName,
          },
        },
      });
    } else {
      await config.httpService.fetch<{
        treatments: TreatmentOverviewDTO[];
      }>({
        method: ApiMethod.PATCH,
        url: config.endpoints.sessionReview.routineFeedback.base,
        data: {
          session_log_id: params.sessionId,
          routine_log_id: params.routineId,
          routine_feedback: {
            text: params.feedback.text,
            media_type: params.feedback.mediaType,
          },
        },
      });
    }

    return params.sessionId;
  },
  async deleteRoutineFeedback(params: { sessionId: string; routineId: number }): Promise<string> {
    await config.httpService.fetch<{
      treatments: TreatmentOverviewDTO[];
    }>({
      method: ApiMethod.DELETE,
      url: config.endpoints.sessionReview.routineFeedback.base,
      data: {
        session_log_id: params.sessionId,
        routine_log_id: params.routineId,
      },
    });

    return params.sessionId;
  },

  async getTreatmentSummary(treatmentId: string): Promise<TreatmentSummaryResponse> {
    const result = await config.httpService.fetch<TreatmentSummaryResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.treatmentSummary(treatmentId),
    });

    return toCamelCase(result);
  },
  async getReviewTask(sessionId: string): Promise<ReviewTaskDTO> {
    const result = await config.httpService.fetch<ReviewTaskDTOApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.reviewTask(sessionId),
    });

    return toCamelCase(result);
  },

  async getRoutineDifficulties(sessionId: string, routineId: number): Promise<RoutineDifficultyOptions> {
    const result = await config.httpService.fetch<RoutineDifficultyOptionsDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.routineDifficulties(sessionId, routineId),
    });

    return routineDifficultyOptionsMapper.toDomain(result);
  },
  async setReviewed(sessionId: string, forwardToSupport: boolean): Promise<string> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.sessionReview.reviewed,
      data: {
        session_log_id: sessionId,
        forward_to_support: forwardToSupport,
      },
    });

    return sessionId;
  },

  async getMistakesText(): Promise<MistakeText[]> {
    const response = await config.cmsService.get<{
      Key: string;
      DisplayedName: string;
      Description: string;
    }>(config.cmsEndpoints.mistakesInfo, { per_page: 100 });
    const stories = response.data.stories;

    return stories.map((s: CMSStory<{ Key: string; DisplayedName: string; Description: string }>) => ({
      key: s.slug,
      name: s.content.DisplayedName ? s.content.DisplayedName : "Displayed Name not found",
      description: s.content.Description ? s.content.Description : "Description not found",
    }));
  },

  async getMistakesVideos(exercise: string): Promise<{ key: string; url: string }[]> {
    const slug = CMS_PATHS.EXERCISE(slugifyCmsKey(exercise));
    const response = await config.cmsService.get(slug);
    const story = response.data.stories[0].content as CmsStoryContent;

    const mistakesVEPool = story.vc_pool?.find(v => v.key === "mistake")?.ve_pool;
    const mappedMistakes =
      mistakesVEPool?.map(v => ({
        key: v.key,
        url: v.media_url.cached_url ?? v.media_url.url,
        duration: v.duration,
      })) || [];
    return mappedMistakes;
  },

  async getFeedbacksSummary(therapistId: string, treatmentId?: string): Promise<FeedbacksSummaryDTO> {
    const result = await config.httpService.fetch<FeedbacksSummaryDTOApi>({
      method: ApiMethod.GET,
      url: config.endpoints.sessionReview.feedbacksSummary(therapistId, treatmentId),
    });

    return toCamelCase(result);
  },
});
